import { Injectable } from '@angular/core';
import { Subscription, timer, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { PortfolioService } from '../../pages/portfolio/portfolio.service';

const DEFAULT_INTERVAL = 60000;
const DEFAULT_DONE_STATUS = 3;
const DEFAULT_FAILED_STATUS = 2;

export interface PortfolioPollingOptions {
  onStatusComplete: (response?: any) => void;
  onStatusFailed?: (response?: any) => void;
  pollingInterval?: number;
  initialDelay?: number;
  onRequestStart?: () => void;
  onRequestEnd?: () => void;
  onError?: (error: any) => void;
  onNext?: (response: any) => void;
  statusDoneValue?: number;
  statusFailedValue?: number;
  listenerKey?: string;
  autoStopOnComplete?: boolean;
  shouldComplete?: (response: any) => boolean;
  context?: any;
}

export interface PortfolioPollingHandle {
  stop: () => void;
}

interface PortfolioPollingListener {
  id: number;
  options: PortfolioPollingOptions;
}

interface PortfolioPoller {
  portfolioId: number;
  intervalMs: number;
  initialDelay: number;
  subscription?: Subscription;
  listeners: Map<number, PortfolioPollingListener>;
  listenerKeys: Map<string, number>;
  isFetching: boolean;
}

@Injectable({ providedIn: 'root' })
export class PortfolioPollingService {
  private pollers = new Map<number, PortfolioPoller>();
  private listenerIdCounter = 0;

  constructor(private portfolioService: PortfolioService) {}

  watchPortfolio(portfolioId: number, options: PortfolioPollingOptions): PortfolioPollingHandle {
    const poller = this.ensurePoller(portfolioId, options);
    const listenerId = this.addListener(poller, options);
    this.startPoller(poller);

    return {
      stop: () => this.removeListener(poller, listenerId)
    };
  }

  stopAll(): void {
    this.pollers.forEach(poller => this.teardownPoller(poller));
    this.pollers.clear();
  }

  stopPortfolio(portfolioId: number): void {
    const poller = this.pollers.get(portfolioId);
    if (poller) {
      this.teardownPoller(poller);
    }
  }

  private ensurePoller(portfolioId: number, options: PortfolioPollingOptions): PortfolioPoller {
    let poller = this.pollers.get(portfolioId);
    if (!poller) {
      poller = {
        portfolioId,
        intervalMs: options.pollingInterval ?? DEFAULT_INTERVAL,
        initialDelay: options.initialDelay ?? 0,
        listeners: new Map<number, PortfolioPollingListener>(),
        listenerKeys: new Map<string, number>(),
        isFetching: false
      };
      this.pollers.set(portfolioId, poller);
    }
    return poller;
  }

  private addListener(poller: PortfolioPoller, options: PortfolioPollingOptions): number {
    if (options.listenerKey) {
      const existingId = poller.listenerKeys.get(options.listenerKey);
      if (existingId !== undefined) {
        poller.listeners.set(existingId, { id: existingId, options });
        return existingId;
      }
    }

    const id = ++this.listenerIdCounter;
    poller.listeners.set(id, { id, options });
    if (options.listenerKey) {
      poller.listenerKeys.set(options.listenerKey, id);
    }
    return id;
  }

  private startPoller(poller: PortfolioPoller): void {
    if (poller.subscription || poller.listeners.size === 0) {
      return;
    }

    poller.subscription = timer(poller.initialDelay, poller.intervalMs)
      .subscribe(() => this.fetchPortfolio(poller));
  }

  private fetchPortfolio(poller: PortfolioPoller): void {
    if (poller.isFetching || poller.listeners.size === 0) {
      return;
    }

    poller.isFetching = true;

    const listeners = Array.from(poller.listeners.values());
    listeners.forEach(listener => listener.options.onRequestStart?.());

    this.portfolioService.getItem(poller.portfolioId, {}, {}, { skipLoader: true })
      .pipe(
        catchError(err => {
          listeners.forEach(listener => listener.options.onError?.(err));
          return of(null);
        }),
        finalize(() => {
          listeners.forEach(listener => listener.options.onRequestEnd?.());
          poller.isFetching = false;
        })
      )
      .subscribe(response => {
        if (!response) {
          return;
        }

        listeners.forEach(listener => listener.options.onNext?.(response));

        const completedListenerIds: number[] = [];
        let shouldStopPoller = true;

        listeners.forEach(listener => {
          const doneValue = listener.options.statusDoneValue ?? DEFAULT_DONE_STATUS;
          const failedValue = listener.options.statusFailedValue ?? DEFAULT_FAILED_STATUS;
          if (response?.item?.status === doneValue) {
            const canComplete = !listener.options.shouldComplete || listener.options.shouldComplete(response);
            if (canComplete) {
              listener.options.onStatusComplete?.(response);
              if (listener.options.autoStopOnComplete !== false) {
                completedListenerIds.push(listener.id);
              }
            } else {
              shouldStopPoller = false;
            }
          } else if (response?.item?.status === failedValue) {
            listener.options.onStatusFailed?.(response);
            completedListenerIds.push(listener.id);
          } else {
            shouldStopPoller = false;
          }
        });

        completedListenerIds.forEach(id => this.removeListener(poller, id));

        if (shouldStopPoller) {
          this.teardownPoller(poller);
        }
      });
  }

  private removeListener(poller: PortfolioPoller, listenerId: number): void {
    const listener = poller.listeners.get(listenerId);
    if (!listener) {
      return;
    }

    if (listener.options.listenerKey) {
      poller.listenerKeys.delete(listener.options.listenerKey);
    }

    poller.listeners.delete(listenerId);

    if (poller.listeners.size === 0) {
      this.teardownPoller(poller);
    }
  }

  removeListenersByContext(context: any): void {
    if (!context) {
      return;
    }

    this.pollers.forEach(poller => {
      const toRemove: number[] = [];
      poller.listeners.forEach(listener => {
        if (listener.options.context === context) {
          toRemove.push(listener.id);
        }
      });
      toRemove.forEach(id => this.removeListener(poller, id));
    });
  }

  private teardownPoller(poller: PortfolioPoller): void {
    poller.subscription?.unsubscribe();
    poller.subscription = undefined;
    poller.isFetching = false;
    const contexts = new Set<any>();
    poller.listeners.forEach(listener => {
      if (listener.options.context) {
        contexts.add(listener.options.context);
      }
    });
    poller.listeners.forEach(listener => {
      if (listener.options.listenerKey) {
        poller.listenerKeys.delete(listener.options.listenerKey);
      }
    });
    poller.listeners.clear();
    poller.listenerKeys.clear();
    this.pollers.delete(poller.portfolioId);
    contexts.forEach(ctx => this.removeListenersByContext(ctx));
  }
}